import { useEffect, useRef } from "react"
import { useStore } from "@tanstack/react-store"
import { useAppForm } from "@/lib/form"

export interface PlaylistFormValues {
  name: string
  icon: string
  color: string
  spotifyPlaylistId: string
}

interface PlaylistFormProps {
  defaultValues?: PlaylistFormValues
  onSubmit: (values: PlaylistFormValues) => void | Promise<void>
  submitLabel?: string
  autoSave?: boolean
}

export function PlaylistForm({
  defaultValues = { name: "", icon: "", color: "", spotifyPlaylistId: "" },
  onSubmit,
  submitLabel = "Create Playlist",
  autoSave = false,
}: PlaylistFormProps) {
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      await onSubmit(value)
    },
  })

  const values = useStore(form.baseStore, (s) => s.values)
  const prevRef = useRef(values)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (!autoSave) return

    const prev = prevRef.current
    prevRef.current = values

    if (!values.name.trim()) return
    if (prev.name === values.name && prev.icon === values.icon && prev.color === values.color && prev.spotifyPlaylistId === values.spotifyPlaylistId) return

    // Icon and color are discrete selections — save immediately
    // Name and spotifyPlaylistId are typed character-by-character — debounce
    const textChanged = prev.name !== values.name || prev.spotifyPlaylistId !== values.spotifyPlaylistId
    const delay = textChanged ? 500 : 0

    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => onSubmit(values), delay)

    return () => clearTimeout(timerRef.current)
  }, [autoSave, values, onSubmit])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      <form.AppField
        name="name"
        validators={{
          onChange: ({ value }) =>
            !value.trim() ? "Name is required" : undefined,
        }}
      >
        {(field) => (
          <field.TextField
            label="Name"
            required
            placeholder="Enter playlist name"
          />
        )}
      </form.AppField>

      <form.AppField name="icon">
        {(field) => (
          <field.EmojiPickerField
            label="Icon"
            placeholder="Pick an icon"
            description="Optional emoji icon for the playlist"
          />
        )}
      </form.AppField>

      <form.AppField name="color">
        {(field) => (
          <field.ColorPickerField
            label="Color"
            description="Optional color accent for the playlist"
          />
        )}
      </form.AppField>

      <form.AppField name="spotifyPlaylistId">
        {(field) => (
          <field.TextField
            label="Spotify Playlist"
            placeholder="https://open.spotify.com/playlist/..."
            description="Link to a Spotify playlist to sync songs automatically"
          />
        )}
      </form.AppField>

      {!autoSave && (
        <form.AppForm>
          <form.SubmitButton label={submitLabel} />
        </form.AppForm>
      )}
    </form>
  )
}
